import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { CourseHero, type CourseTab } from "@/components/courses/CourseHero";
import { CourseContent } from "@/components/courses/CourseContent";
import { CourseSourcesView } from "@/components/courses/CourseSourcesView";
import { CourseGraphDialog } from "@/components/courses/CourseGraphDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCourseData } from "@/hooks/useCourseData";
import { computeNodeStates } from "@/lib/courses/computeNodeStates";
import { enrollInCourse } from "@/lib/courses/enrollInCourse";
import { addDays, format } from "date-fns";

const CoursePageSkeleton: React.FC = () => (
  <div className="flex-1 overflow-y-auto p-6 space-y-6">
    <Skeleton className="h-48 w-full rounded-xl" />
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="w-full lg:w-[380px] space-y-4">
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-56 w-full rounded-xl" />
      </div>
      <div className="flex-1 space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    </div>
  </div>
);

const CoursePage: React.FC = () => {
  usePageMeta("Course — Shattara AI", "", { noIndex: true });
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [graphOpen, setGraphOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CourseTab>("course");

  const {
    course,
    nodes,
    contentStepCounts,
    courseProgress,
    nodeProgressMap,
    weeklyXp,
    isLoading,
    isError,
    userId,
  } = useCourseData(courseId);

  // Auto-enroll user on first visit
  useEffect(() => {
    if (userId && courseId && course && !courseProgress && !isLoading) {
      enrollInCourse(userId, courseId).catch(console.error);
    }
  }, [userId, courseId, course, courseProgress, isLoading]);

  // Redirect on error (course not found)
  useEffect(() => {
    if (isError) {
      navigate("/dashboard", { replace: true });
    }
  }, [isError, navigate]);

  // Compute node states from real data
  const nodesWithStatus = useMemo(
    () => (course ? computeNodeStates(nodes, nodeProgressMap, contentStepCounts) : []),
    [course, nodes, nodeProgressMap, contentStepCounts],
  );

  const completedCount = nodesWithStatus.filter((n) => n.status === "completed").length;
  const progress = nodes.length > 0 ? (completedCount / nodes.length) * 100 : 0;
  const totalXp = courseProgress?.totalXp ?? 0;
  const todayStr = new Date().toISOString().split("T")[0];
  const todayXp = weeklyXp.find((d) => d.date === todayStr)?.xp ?? 0;

  // Estimated completion calculation
  const estimatedCompletion = useMemo(() => {
    const remaining = nodes.length - completedCount;
    if (remaining <= 0) return "Complete!";

    const totalWeeklyXp = weeklyXp.reduce((sum, d) => sum + d.xp, 0);
    const daysWithXp = weeklyXp.filter((d) => d.xp > 0).length;
    if (daysWithXp === 0 || totalXp === 0) return "Start a lesson to estimate";

    const avgXpPerDay = totalWeeklyXp / daysWithXp;
    const avgXpPerNode = completedCount > 0 ? totalXp / completedCount : avgXpPerDay;
    const remainingXp = remaining * avgXpPerNode;
    const daysNeeded = Math.ceil(remainingXp / avgXpPerDay);
    const targetDate = addDays(new Date(), daysNeeded);
    return format(targetDate, "MMM d");
  }, [nodes.length, completedCount, weeklyXp, totalXp]);

  if (isLoading || !course) {
    return (
      <DashboardShell>
        <CoursePageSkeleton />
      </DashboardShell>
    );
  }

  const handleNodeAction = (nodeId: string) => {
    navigate(`/course/${courseId}/lesson/${nodeId}`);
  };

  return (
    <DashboardShell>
      <div className="flex-1 overflow-y-auto">
        <CourseHero course={course} activeTab={activeTab} onTabChange={setActiveTab} />
        {activeTab === "course" ? (
          <CourseContent
            nodes={nodesWithStatus}
            courseName={course.title}
            courseId={courseId!}
            progress={progress}
            estimatedCompletion={estimatedCompletion}
            totalXp={totalXp}
            todayXp={todayXp}
            dailyTarget={250}
            weeklyXp={weeklyXp}
            contentStepCounts={contentStepCounts}
            onNodeAction={handleNodeAction}
            onOpenGraph={() => setGraphOpen(true)}
          />
        ) : (
          <CourseSourcesView
            courseId={courseId!}
            sourceContentIds={course.sourceContentIds ?? []}
          />
        )}
      </div>
      <CourseGraphDialog
        open={graphOpen}
        onOpenChange={setGraphOpen}
        nodes={nodesWithStatus}
      />
    </DashboardShell>
  );
};

export default CoursePage;
